/**
 * Connection Coordinator
 * Manages connection states across different wallet types to prevent conflicts
 * and improve overall connection reliability
 */

interface ConnectionState {
  isConnecting: boolean;
  lastAttempt: number;
  source: "metamask" | "solana" | "other";
  priority: number; // Higher priority wins
}

interface ConnectionEvent {
  type:
    | "connect-initiated"
    | "connect-success"
    | "connect-failed"
    | "disconnect";
  source: "metamask" | "solana" | "other";
  timestamp: number;
}

class ConnectionCoordinator {
  private currentState: ConnectionState | null = null;
  private eventHistory: ConnectionEvent[] = [];
  private readonly CONFLICT_WINDOW = 5000; // 5 seconds
  private readonly MAX_HISTORY = 20;

  // Priority levels: higher = more priority
  private readonly PRIORITIES = {
    metamask: 10,
    solana: 5,
    other: 1,
  };

  /**
   * Request permission to start a connection attempt
   */
  public requestConnection(source: "metamask" | "solana" | "other"): boolean {
    const now = Date.now();
    const sourcePriority = this.PRIORITIES[source];

    // Clean old history
    this.cleanHistory(now);

    // Check if another connection is in progress
    if (this.currentState && this.currentState.isConnecting) {
      const timeSinceLastAttempt = now - this.currentState.lastAttempt;

      // If current connection is old, allow override
      if (timeSinceLastAttempt > this.CONFLICT_WINDOW) {
        console.log(
          `ConnectionCoordinator: Allowing ${source} to override stale ${this.currentState.source} connection`
        );
        this.currentState = null;
      } else {
        // Check priority
        if (sourcePriority <= this.currentState.priority) {
          console.log(
            `ConnectionCoordinator: Blocking ${source} connection, ${this.currentState.source} has priority`
          );
          return false;
        } else {
          console.log(
            `ConnectionCoordinator: Allowing ${source} to override lower priority ${this.currentState.source} connection`
          );
        }
      }
    }

    // Check for recent failed attempts from the same source
    const recentFailures = this.eventHistory.filter(
      (event) =>
        event.source === source &&
        event.type === "connect-failed" &&
        now - event.timestamp < this.CONFLICT_WINDOW
    );

    if (recentFailures.length >= 3) {
      console.log(
        `ConnectionCoordinator: Too many recent failures for ${source}, blocking connection`
      );
      return false;
    }

    // Set new connection state
    this.currentState = {
      isConnecting: true,
      lastAttempt: now,
      source,
      priority: sourcePriority,
    };

    this.addEvent({
      type: "connect-initiated",
      source,
      timestamp: now,
    });

    console.log(
      `ConnectionCoordinator: Approved connection request for ${source}`
    );
    return true;
  }

  /**
   * Report connection success
   */
  public reportSuccess(source: "metamask" | "solana" | "other"): void {
    if (this.currentState?.source === source) {
      this.currentState.isConnecting = false;
    }

    this.addEvent({
      type: "connect-success",
      source,
      timestamp: Date.now(),
    });

    console.log(
      `ConnectionCoordinator: Connection success reported for ${source}`
    );
  }

  /**
   * Report connection failure
   */
  public reportFailure(source: "metamask" | "solana" | "other"): void {
    if (this.currentState?.source === source) {
      this.currentState.isConnecting = false;
    }

    this.addEvent({
      type: "connect-failed",
      source,
      timestamp: Date.now(),
    });

    console.log(
      `ConnectionCoordinator: Connection failure reported for ${source}`
    );
  }

  /**
   * Report disconnection
   */
  public reportDisconnect(source: "metamask" | "solana" | "other"): void {
    if (this.currentState?.source === source) {
      this.currentState = null;
    }

    this.addEvent({
      type: "disconnect",
      source,
      timestamp: Date.now(),
    });

    console.log(`ConnectionCoordinator: Disconnection reported for ${source}`);
  }

  /**
   * Check if a source should wait before attempting connection
   */
  public shouldWait(source: "metamask" | "solana" | "other"): number {
    const now = Date.now();

    // Check for recent activity from other sources
    const recentActivity = this.eventHistory.filter(
      (event) =>
        event.source !== source && now - event.timestamp < this.CONFLICT_WINDOW
    );

    if (recentActivity.length > 0) {
      const mostRecent = Math.max(...recentActivity.map((e) => e.timestamp));
      const waitTime = this.CONFLICT_WINDOW - (now - mostRecent);
      return Math.max(0, waitTime);
    }

    return 0;
  }

  /**
   * Get current connection state summary
   */
  public getState(): {
    isConnecting: boolean;
    source?: string;
    waitTime: number;
    recentActivity: ConnectionEvent[];
  } {
    const now = Date.now();
    return {
      isConnecting: this.currentState?.isConnecting || false,
      source: this.currentState?.source,
      waitTime: this.currentState
        ? Math.max(
            0,
            this.CONFLICT_WINDOW - (now - this.currentState.lastAttempt)
          )
        : 0,
      recentActivity: this.eventHistory.filter(
        (event) => now - event.timestamp < this.CONFLICT_WINDOW
      ),
    };
  }

  /**
   * Reset coordinator state
   */
  public reset(): void {
    this.currentState = null;
    this.eventHistory = [];
    console.log("ConnectionCoordinator: State reset");
  }

  private addEvent(event: ConnectionEvent): void {
    this.eventHistory.push(event);

    // Keep history size manageable
    if (this.eventHistory.length > this.MAX_HISTORY) {
      this.eventHistory = this.eventHistory.slice(-this.MAX_HISTORY);
    }
  }

  private cleanHistory(now: number): void {
    // Remove events older than the conflict window
    this.eventHistory = this.eventHistory.filter(
      (event) => now - event.timestamp < this.CONFLICT_WINDOW * 2
    );
  }
}

// Create singleton instance
const connectionCoordinator = new ConnectionCoordinator();

export default connectionCoordinator;

// Export utility functions
export const requestWalletConnection = (
  source: "metamask" | "solana" | "other"
) => connectionCoordinator.requestConnection(source);

export const reportConnectionSuccess = (
  source: "metamask" | "solana" | "other"
) => connectionCoordinator.reportSuccess(source);

export const reportConnectionFailure = (
  source: "metamask" | "solana" | "other"
) => connectionCoordinator.reportFailure(source);

export const reportWalletDisconnect = (
  source: "metamask" | "solana" | "other"
) => connectionCoordinator.reportDisconnect(source);

export const getConnectionWaitTime = (
  source: "metamask" | "solana" | "other"
) => connectionCoordinator.shouldWait(source);

export const getConnectionState = () => connectionCoordinator.getState();
